import {
  startTransition,
  useCallback,
  useMemo,
  useState,
  useTransition,
} from "react";
import type {
  Process,
  RollupWithTagsAndCountsAndRange,
} from "../../types/types";
import {
  clearAllTrendChartBars,
  clearAllAggregates,
  updateRollupsForProcessTrendChartBars,
} from "../../providers/GlobalStateProvider";
import { askrClients } from "../../providers/AskrProvider";
import {
  isLargeQueryError,
  useQueryAndUpdateMultipleAggregates,
  getIsShiftlyQuery,
  AGGREGATE_ON_FREQUENCY_QUERY,
  aggregateOnFrequencyShiftlyQuery,
} from "./aggregatesGetterUtils";
import {
  useFocusRange,
  useTrendsPageFilterState,
  useTrendChartBinSize,
  useGetPageFromPath,
  useLoadWhenValuesChange,
  useInterval,
} from "utils/customHooks/genericHooks";
import { useDispatch } from "react-redux";
import { AGGREGATES_FOR_TREND_CHART_BARS_QUERY_TITLE } from "contexts/ErrorManager/errorManagerUtils";
import { NO_SHIFT_SCHEDULED } from "components/Dashboard/range-selectors/steppable-range-selectors/ShiftSelector/shiftUtils";
import { CHECK_CURRENT_TREND_CHART_BARS_INTERVAL } from "constants/intervals";
import { useTrendChartStore } from "components/Dashboard/TrendChart/TrendChartCardControls/trendChartStore";
import { ONE_DAY_OF_MS } from "utils/timeUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useFilteredShifts } from "utils/customHooks/trendChartHooks";

type AggregateGetterTrendChartBarsProps = {
  process: Process;
  queryTitle: string;
  pollInterval: number;
  shouldQuery: boolean;
};
export const AggregateGetterTrendChartBars = ({
  process,
  queryTitle,
  pollInterval,
  shouldQuery,
}: AggregateGetterTrendChartBarsProps) => {
  const dispatch = useDispatch();
  const focusRange = useFocusRange();
  const [now, setNow] = useState(Date.now());
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();
  const { shouldAlwaysStartTrendChartBarsOnHour } = useTrendChartStore();

  // check again every 30s
  useInterval({
    callback: () => {
      setNow(Date.now());
    },
    delay:
      CHECK_CURRENT_TREND_CHART_BARS_INTERVAL * multiplyNetworkRequestIntervals,
    immediate: false,
  });
  // special behaviour if we're looking at trends page filtered by shift
  const isShiftlyQuery = useIsShiftlyQuery();
  const { trendChartBinSize } = useTrendChartBinSize();
  const duration = focusRange ? focusRange.lt - focusRange.ge : 0;
  const {
    trendsPageFilterState: { shiftName },
    isTrendsPageFilteredByShift,
  } = useTrendsPageFilterState();

  // TODO check current query limits, seems to only work for <ONE_DAY currently
  const isTrendsPage24HrShiftMode =
    isTrendsPageFilteredByShift && duration <= ONE_DAY_OF_MS;
  const filteredShifts = useFilteredShifts();

  // need to refetch trend chart bars when the bin size changes
  const didDependencyChange = useLoadWhenValuesChange({
    valuesWhichChangeArr: [
      trendChartBinSize,
      isTrendsPage24HrShiftMode,
      isShiftlyQuery,
      focusRange,
    ],
    isInitiallyLoading: false,
    loadingInterval: 500,
    componentNameForDebugging: "AggregateGetterTrendChartBars",
  });

  const { ckey } = process;
  const client = askrClients.get(ckey);

  const variables = useMemo(
    () => ({
      machemetrySourceName: process.id,
      ge: focusRange?.ge.toString(),
      lt: focusRange?.lt.toString(),
      // for shiftly queries we get all the shift aggregates back, and roll them up in Flight
      ...(isShiftlyQuery
        ? {}
        : {
            aggregateOnFrequencyType:
              AGGREGATE_ON_FREQUENCY_ARGUMENT_MAP[trendChartBinSize],
            startingOnMinute: shouldAlwaysStartTrendChartBarsOnHour
              ? 0
              : new Date(Number(focusRange?.ge)).getMinutes(),
          }),
    }),
    [
      focusRange?.ge,
      focusRange?.lt,
      process.id,
      trendChartBinSize,
      shouldAlwaysStartTrendChartBarsOnHour,
      isShiftlyQuery,
    ]
  );
  const trendsPageFilteredShift = filteredShifts.find(
    shift =>
      focusRange &&
      shift.shift.name === shiftName &&
      shift.range.ge >= focusRange.ge &&
      shift.range.ge < focusRange.lt
  );
  const range = useMemo(
    () =>
      isTrendsPage24HrShiftMode && trendsPageFilteredShift
        ? {
            ge: trendsPageFilteredShift.range.ge,
            lt: trendsPageFilteredShift.range.lt,
          }
        : {
            ge: variables.ge,
            lt: Math.min(Number(variables.lt), now).toString(),
          },
    [
      variables.ge,
      variables.lt,
      now,
      isTrendsPage24HrShiftMode,
      trendsPageFilteredShift,
    ]
  ); // update lt to Date.now() for next poll
  const isInvertedTimeRange = Number(range.lt) < Number(range.ge);

  // skip = don't fetch,
  // or toggle one of these to false -> true to refetch (e.g. didDependencyChange)
  const skip =
    !focusRange ||
    !shouldQuery ||
    isInvertedTimeRange ||
    didDependencyChange ||
    (isTrendsPage24HrShiftMode && !trendsPageFilteredShift);

  const dispatchFn = useCallback(
    (rollups: RollupWithTagsAndCountsAndRange[]) => {
      return startTransition(() => {
        dispatch(
          updateRollupsForProcessTrendChartBars(
            process.id,
            isTrendsPage24HrShiftMode || isShiftlyQuery
              ? // if it's a shiftly query, populate empty shift named with No Shift Scheduled
                rollups.map(rollup => ({
                  ...rollup,
                  shift: {
                    ...rollup.shift,
                    ...(isTrendsPage24HrShiftMode
                      ? // in 24-hour mode (24 1-hour bins),

                        // we append the shift directly from the shifts array in the current range
                        {
                          name: shiftName,
                          ...(trendsPageFilteredShift?.range
                            ? { range: trendsPageFilteredShift.range }
                            : {}),
                        }
                      : // in other shift modes, we get the shift back from askr, but add "Shift Not Specified" if null
                        {
                          name: rollup.shift?.name || NO_SHIFT_SCHEDULED,
                        }),
                  },
                }))
              : rollups
          )
        );
      });
    },
    [
      dispatch,
      isShiftlyQuery,
      isTrendsPage24HrShiftMode,
      process.id,
      shiftName,
      trendsPageFilteredShift,
    ]
  );

  if (!focusRange) {
    return null;
  }

  const query = isShiftlyQuery
    ? aggregateOnFrequencyShiftlyQuery
    : AGGREGATE_ON_FREQUENCY_QUERY;

  return skip ? null : (
    <QueryTrendChartBars
      {...{
        query,
        variables,
        range: isTrendsPage24HrShiftMode
          ? {
              ge: trendsPageFilteredShift?.range.ge.toString(),
              lt: trendsPageFilteredShift?.range.lt.toString(),
            }
          : range,
        dispatchFn,
        pollInterval,
        client,
        queryTitle,
      }}
    />
  );
};
const AGGREGATE_ON_FREQUENCY_ARGUMENT_MAP = {
  hours: "HOURLY",
  days: "DAILY",
  shifts: "SHIFTLY",
  weeks: "WEEKLY",
};
function QueryTrendChartBars({
  query,
  variables,
  range,
  dispatchFn,
  pollInterval,
  queryTitle,
  client,
}: any) {
  const [, startTransition] = useTransition();
  const dispatch = useDispatch();
  useQueryAndUpdateMultipleAggregates({
    query,
    variables: { ...variables, ...range },
    dispatchFn,
    pollInterval,
    onError: err => {
      // if we get an error back for trend chart bars, clear the bars
      if (isLargeQueryError(err)) {
        startTransition(() => {
          dispatch(
            queryTitle === AGGREGATES_FOR_TREND_CHART_BARS_QUERY_TITLE
              ? clearAllTrendChartBars()
              : clearAllAggregates()
          );
        });
        // if we get one error, stop querying
        // setShouldQuery(false);
      }
    },
    client,
    queryTitle,
  });
  return null;
}
function useIsShiftlyQuery(): boolean {
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const focusRange = useFocusRange();
  const trendsPageFilterStateShiftName = trendsPageFilterState.shiftName;
  const { isTrendsPage } = useGetPageFromPath();

  return getIsShiftlyQuery(
    trendsPageFilterStateShiftName,
    focusRange,
    isTrendsPage
  );
}
